// src/features/streams/components/schedule/ScheduleEditorModal.tsx

import { useMemo, useState } from 'react';
import {
  Modal,
  Stack,
  Group,
  Box,
  Button,
  Chip,
  Select,
  NumberInput,
  TextInput,
  Textarea,
  Switch,
  Divider,
  Text,
  Alert,
} from '@mantine/core';
import { DateTimeField } from '@/shared/components/DateTimeField';
import { IconAlertCircle } from '@tabler/icons-react';
import { useForm, useController } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { DateTime, Info } from 'luxon';
import {
  SCHEDULE_INTERVAL_TYPE_LABELS,
  SCHEDULE_INTERVAL_TYPES,
  SCHEDULE_INTERVAL_MAX,
  SCHEDULE_INTERVAL_UNIT_LABELS,
  STREAM_SCHEDULE_CONSTRAINTS,
} from '@/lib/constants/stream';
import { TagInput } from '@/shared/components/TagInput';
import { useTags } from '@/features/tag/useTags';
import { makeScheduleFormSchema, type ScheduleFormData } from '../../schemas/ScheduleForm.schema';
import { StreamScheduleUtils } from '../../StreamScheduleUtils';

interface ScheduleEditorModalProps {
  opened: boolean;
  mode: 'create' | 'edit';
  defaultValues: ScheduleFormData;
  /** IANA zone all schedule times are anchored to (schema factory + previews) */
  zoneId: string;
  /** Fixed at modal open — schema refinements and occurrence previews use it */
  nowSecs: number;
  /**
   * Parent runs the conflict check + mutation. extraWeekdays (Luxon 1=Mon…7=Sun)
   * is non-empty only for weekly creates: one extra weekly schedule per day.
   */
  onSubmit: (data: ScheduleFormData, extraWeekdays: number[]) => void;
  onCancel: () => void;
  isPending: boolean;
  /** Conflict message from the parent, rendered above the footer */
  rootError: string | null;
}

const REPEAT_OPTIONS = [
  { value: 'never', label: 'Does not repeat' },
  ...SCHEDULE_INTERVAL_TYPES.map((t) => ({ value: t, label: SCHEDULE_INTERVAL_TYPE_LABELS[t] })),
];

/** Mon…Sun, indexed by Luxon weekday - 1; app locale, not machine locale */
const WEEKDAY_LABELS = Info.weekdays('short', { locale: 'en-US' });

export function ScheduleEditorModal({
  opened,
  mode,
  defaultValues,
  zoneId,
  nowSecs,
  onSubmit,
  onCancel,
  isPending,
  rootError,
}: ScheduleEditorModalProps) {
  return (
    <Modal
      opened={opened}
      onClose={onCancel}
      title={<Text fw={600}>{mode === 'create' ? 'Add Recording Schedule' : 'Edit Recording Schedule'}</Text>}
      size="lg"
      closeOnClickOutside={!isPending}
      closeOnEscape={!isPending}
    >
      {opened && (
        <ScheduleEditorForm
          mode={mode}
          defaultValues={defaultValues}
          zoneId={zoneId}
          nowSecs={nowSecs}
          onSubmit={onSubmit}
          onCancel={onCancel}
          isPending={isPending}
          rootError={rootError}
        />
      )}
    </Modal>
  );
}

/** Mounted per open so useForm re-initializes from defaultValues. */
function ScheduleEditorForm({
  mode,
  defaultValues,
  zoneId,
  nowSecs,
  onSubmit,
  onCancel,
  isPending,
  rootError,
}: Omit<ScheduleEditorModalProps, 'opened'>) {
  const { tags: allTags } = useTags();

  const schema = useMemo(() => makeScheduleFormSchema(zoneId, nowSecs), [zoneId, nowSecs]);
  const { control, handleSubmit, watch, setValue } = useForm<ScheduleFormData>({
    resolver: zodResolver(schema),
    defaultValues,
  });

  const startField = useController({ control, name: 'startStr' });
  const endField = useController({ control, name: 'endStr' });
  const repeatsField = useController({ control, name: 'repeats' });
  const intervalField = useController({ control, name: 'interval' });
  const overrideTitleField = useController({ control, name: 'overrideTitle' });
  const titleField = useController({ control, name: 'title' });
  const overrideDescField = useController({ control, name: 'overrideDesc' });
  const descField = useController({ control, name: 'desc' });
  const overrideTagsField = useController({ control, name: 'overrideTags' });
  const tagsField = useController({ control, name: 'tags' });

  const watchedStart = watch('startStr');
  const watchedEnd = watch('endStr');
  const watchedRepeats = watch('repeats');
  const watchedInterval = watch('interval');

  const isRecurring = watchedRepeats !== 'never';
  const intervalUnit = isRecurring ? SCHEDULE_INTERVAL_UNIT_LABELS[watchedRepeats] : '';
  const intervalMax = isRecurring ? SCHEDULE_INTERVAL_MAX[watchedRepeats] : 1;

  const startSecs = StreamScheduleUtils.dateStrToUtcSeconds(watchedStart ?? '', zoneId);
  const endSecs = StreamScheduleUtils.dateStrToUtcSeconds(watchedEnd ?? '', zoneId);
  const hasValidWindow = Number.isFinite(startSecs) && Number.isFinite(endSecs) && endSecs > startSecs;

  const durationLabel = hasValidWindow ? StreamScheduleUtils.formatDuration(startSecs, endSecs) : '';

  // --- Multi-day weekly create: extra weekday toggles (Luxon 1=Mon…7=Sun) ---
  const [extraDays, setExtraDays] = useState<number[]>([]);
  const anchorWeekday = hasValidWindow
    ? DateTime.fromSeconds(startSecs, { zone: zoneId }).weekday
    : null;
  const showMultiDay = mode === 'create' && watchedRepeats === 'w' && anchorWeekday !== null;
  // The anchor day tracks the start date — drop it from extras if they collide
  const selectedExtraDays = anchorWeekday === null ? [] : extraDays.filter((d) => d !== anchorWeekday);
  // Chip order starts at the start date's weekday (matches how users think about the week)
  const orderedWeekdays =
    anchorWeekday === null
      ? []
      : Array.from({ length: 7 }, (_, i) => ((anchorWeekday - 1 + i) % 7) + 1);
  const scheduleCount = 1 + (showMultiDay ? selectedExtraDays.length : 0);

  // Next-occurrences preview (D6: weekday is explicit in each occurrence)
  const previewText = useMemo(() => {
    if (!hasValidWindow) return null;
    if (watchedRepeats === 'never') {
      return `Records once: ${StreamScheduleUtils.formatScheduleWindow({ startUtc: startSecs, endUtc: endSecs }, zoneId)}`;
    }
    if (!watchedInterval || watchedInterval < 1) return null;
    if (showMultiDay && selectedExtraDays.length > 0) {
      const days = orderedWeekdays
        .filter((d) => d === anchorWeekday || selectedExtraDays.includes(d))
        .map((d) => WEEKDAY_LABELS[d - 1])
        .join(', ');
      const time = DateTime.fromSeconds(startSecs, { zone: zoneId })
        .setLocale('en-US')
        .toFormat('h:mm a');
      const cadence = watchedInterval === 1 ? 'weekly' : `every ${watchedInterval} weeks`;
      return `Records ${cadence} on ${days} · ${time} (creates ${scheduleCount} weekly schedules)`;
    }
    const occurrences = StreamScheduleUtils.getUpcomingOccurrences(
      { startUtc: startSecs, endUtc: endSecs, interval: watchedInterval, intervalType: watchedRepeats },
      zoneId,
      nowSecs,
      3,
    );
    return `Next: ${occurrences.map((ts) => StreamScheduleUtils.formatOccurrence(ts, zoneId)).join(' · ')}`;
  }, [hasValidWindow, startSecs, endSecs, watchedInterval, watchedRepeats, zoneId, nowSecs,
    showMultiDay, selectedExtraDays, orderedWeekdays, anchorWeekday, scheduleCount]);

  // Legacy bound: schedules can start at most ~13 months out
  const maxStartDate = useMemo(
    () =>
      DateTime.fromSeconds(nowSecs, { zone: zoneId })
        .plus({ days: STREAM_SCHEDULE_CONSTRAINTS.MAX_FUTURE_DAYS })
        .toFormat('yyyy-MM-dd'),
    [nowSecs, zoneId],
  );

  // stopPropagation: this form is DOM-portaled but still a React child of the
  // Overview <form> — without it the submit event bubbles through the React
  // tree and fires the Overview form's save on top of this one.
  return (
    <form
      onSubmit={(e) => {
        e.stopPropagation();
        void handleSubmit((data) =>
          onSubmit(data, mode === 'create' && data.repeats === 'w' ? selectedExtraDays : []),
        )(e);
      }}
    >
      <Stack gap="md">
        <DateTimeField
          label="Start"
          value={startField.field.value}
          onChange={(val) => {
            if (val) startField.field.onChange(val);
          }}
          onBlur={startField.field.onBlur}
          error={startField.fieldState.error?.message}
          valueFormat="MMM D, YYYY h:mm A"
          timePickerProps={{ format: '12h' }}
          maxDate={maxStartDate}
        />

        <DateTimeField
          label="End"
          description={durationLabel ? `Duration: ${durationLabel} (max ${STREAM_SCHEDULE_CONSTRAINTS.MAX_DURATION_HOURS} hours)` : undefined}
          value={endField.field.value}
          onChange={(val) => {
            if (val) endField.field.onChange(val);
          }}
          onBlur={endField.field.onBlur}
          error={endField.fieldState.error?.message}
          valueFormat="MMM D, YYYY h:mm A"
          timePickerProps={{ format: '12h' }}
        />

        <Group grow align="flex-start">
          <Select
            label="Repeats"
            data={REPEAT_OPTIONS}
            value={repeatsField.field.value}
            onChange={(val) => {
              if (!val) return;
              repeatsField.field.onChange(val);
              setValue('interval', 1);
            }}
            onBlur={repeatsField.field.onBlur}
            allowDeselect={false}
          />

          {isRecurring && (
            <NumberInput
              label="Repeat every"
              value={intervalField.field.value}
              onChange={(val) => {
                if (typeof val === 'number') intervalField.field.onChange(val);
              }}
              onBlur={intervalField.field.onBlur}
              error={intervalField.fieldState.error?.message}
              min={1}
              max={intervalMax}
              clampBehavior="strict"
              allowDecimal={false}
              suffix={` ${intervalUnit}${watchedInterval === 1 ? '' : 's'}`}
            />
          )}
        </Group>

        {showMultiDay && (
          <Box>
            <Text size="sm" fw={500} mb={6}>Repeats on</Text>
            <Group gap={6}>
              {orderedWeekdays.map((day) => {
                const isAnchor = day === anchorWeekday;
                return (
                  <Chip
                    key={day}
                    size="xs"
                    checked={isAnchor || selectedExtraDays.includes(day)}
                    disabled={isAnchor}
                    onChange={(nowChecked) =>
                      setExtraDays((prev) =>
                        nowChecked ? [...prev, day] : prev.filter((d) => d !== day),
                      )
                    }
                  >
                    {WEEKDAY_LABELS[day - 1]}
                  </Chip>
                );
              })}
            </Group>
            <Text size="xs" c="dimmed" mt={6}>
              The start date&apos;s weekday is always included. Each additional day becomes its
              own weekly schedule at the same time.
            </Text>
          </Box>
        )}

        {previewText && (
          <Text size="sm" c="dimmed">{previewText}</Text>
        )}

        <Divider label="Recorded video details" labelPosition="left" />
        <Text size="sm" c="dimmed">
          By default the recording inherits the stream&apos;s title, description, and tags.
        </Text>

        <Switch
          label="Custom title"
          checked={overrideTitleField.field.value}
          onChange={(e) => overrideTitleField.field.onChange(e.currentTarget.checked)}
        />
        {overrideTitleField.field.value && (
          <TextInput
            label="Title"
            description={`${titleField.field.value.length}/${STREAM_SCHEDULE_CONSTRAINTS.TITLE_MAX}`}
            value={titleField.field.value}
            onChange={titleField.field.onChange}
            onBlur={titleField.field.onBlur}
            error={titleField.fieldState.error?.message}
            maxLength={STREAM_SCHEDULE_CONSTRAINTS.TITLE_MAX}
            withAsterisk
          />
        )}

        <Switch
          label="Custom description"
          checked={overrideDescField.field.value}
          onChange={(e) => overrideDescField.field.onChange(e.currentTarget.checked)}
        />
        {overrideDescField.field.value && (
          <Textarea
            label="Description"
            description={`${descField.field.value.length}/${STREAM_SCHEDULE_CONSTRAINTS.DESC_MAX}`}
            value={descField.field.value}
            onChange={descField.field.onChange}
            onBlur={descField.field.onBlur}
            error={descField.fieldState.error?.message}
            maxLength={STREAM_SCHEDULE_CONSTRAINTS.DESC_MAX}
            rows={3}
          />
        )}

        <Switch
          label="Custom tags"
          checked={overrideTagsField.field.value}
          onChange={(e) => overrideTagsField.field.onChange(e.currentTarget.checked)}
        />
        {overrideTagsField.field.value && (
          <TagInput
            value={tagsField.field.value}
            onChange={tagsField.field.onChange}
            onBlur={tagsField.field.onBlur}
            allTags={allTags}
          />
        )}

        {rootError && (
          <Alert icon={<IconAlertCircle size={16} />} color="red" variant="light">
            {rootError}
          </Alert>
        )}

        <Group justify="flex-end" mt="sm">
          <Button type="button" variant="outline" onClick={onCancel} disabled={isPending}>
            Cancel
          </Button>
          <Button type="submit" loading={isPending}>
            {mode === 'create'
              ? scheduleCount > 1
                ? `Add ${scheduleCount} Schedules`
                : 'Add Schedule'
              : 'Save Changes'}
          </Button>
        </Group>
      </Stack>
    </form>
  );
}
